import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { useToast } from '../hooks/use-toast';
import { Loader2, X } from 'lucide-react';

const countryCodes = [
  { code: '+1', country: 'US/Canada', flag: '🇺🇸' },
  { code: '+44', country: 'UK', flag: '🇬🇧' },
  { code: '+91', country: 'India', flag: '🇮🇳' },
  { code: '+86', country: 'China', flag: '🇨🇳' },
  { code: '+81', country: 'Japan', flag: '🇯🇵' },
  { code: '+49', country: 'Germany', flag: '🇩🇪' },
  { code: '+33', country: 'France', flag: '🇫🇷' },
  { code: '+39', country: 'Italy', flag: '🇮🇹' },
  { code: '+61', country: 'Australia', flag: '🇦🇺' },
  { code: '+55', country: 'Brazil', flag: '🇧🇷' },
  { code: '+52', country: 'Mexico', flag: '🇲🇽' },
  { code: '+7', country: 'Russia', flag: '🇷🇺' },
  { code: '+82', country: 'South Korea', flag: '🇰🇷' },
  { code: '+34', country: 'Spain', flag: '🇪🇸' },
  { code: '+31', country: 'Netherlands', flag: '🇳🇱' },
  { code: '+46', country: 'Sweden', flag: '🇸🇪' },
  { code: '+47', country: 'Norway', flag: '🇳🇴' },
  { code: '+41', country: 'Switzerland', flag: '🇨🇭' },
  { code: '+65', country: 'Singapore', flag: '🇸🇬' },
  { code: '+971', country: 'UAE', flag: '🇦🇪' },
  { code: '+966', country: 'Saudi Arabia', flag: '🇸🇦' },
  { code: '+27', country: 'South Africa', flag: '🇿🇦' },
  { code: '+64', country: 'New Zealand', flag: '🇳🇿' },
  { code: '+351', country: 'Portugal', flag: '🇵🇹' },
  { code: '+48', country: 'Poland', flag: '🇵🇱' },
];

const ContactFormDialog = ({ isOpen, onClose }) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    phone: '',
    description: ''
  });
  const [errors, setErrors] = useState({
    email: '',
    phone: ''
  });

  // Email validation
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Phone validation - only digits, max 10
  const validatePhone = (phone) => {
    const phoneRegex = /^\d{0,10}$/;
    return phoneRegex.test(phone);
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (field === 'email' || field === 'phone') {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const handleEmailBlur = () => {
    if (formData.email && !validateEmail(formData.email)) {
      setErrors(prev => ({ ...prev, email: 'Please enter a valid email address (e.g., user@example.com)' }));
    }
  };

  const handlePhoneChange = (value) => {
    // Remove non-digits
    const digitsOnly = value.replace(/\D/g, '');
    
    // Limit to 10 digits
    if (digitsOnly.length <= 10) {
      handleChange('phone', digitsOnly);
      
      if (digitsOnly && digitsOnly.length !== 10) {
        setErrors(prev => ({ ...prev, phone: 'Phone number must be exactly 10 digits' }));
      } else {
        setErrors(prev => ({ ...prev, phone: '' }));
      }
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Final validation
    let hasErrors = false;
    const newErrors = { email: '', phone: '' };

    if (!validateEmail(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
      hasErrors = true;
    }

    if (formData.phone.length !== 10) {
      newErrors.phone = 'Phone number must be exactly 10 digits';
      hasErrors = true;
    }

    if (hasErrors) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      toast({
        title: "Request Submitted Successfully!",
        description: "Our trademark experts will contact you within 24 hours to discuss your brand protection needs.",
      });
      setIsSubmitting(false);
      setFormData({
        name: '',
        email: '',
        countryCode: '+1',
        phone: '',
        description: ''
      });
      setErrors({ email: '', phone: '' });
      onClose();
    }, 1500);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-[1100px] p-0 gap-0 overflow-hidden max-h-[90vh]">
        <div className="flex flex-col md:flex-row h-full max-h-[90vh]">
          {/* Left Side - Image */}
          <div className="relative w-full md:w-[45%] h-64 md:h-auto min-h-[500px] bg-[#0F1419]">
            <img
              src="https://images.unsplash.com/photo-1689360699724-a9fcbd650c5a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwzfHx0cmFkZW1hcmslMjBjb3B5cmlnaHQlMjBsZWdhbCUyMHByb3RlY3Rpb24lMjAzZCUyMHJlbmRlciUyMGRhcmt8ZW58MHx8fHwxNzc1MDc4MjI0fDA&ixlib=rb-4.1.0&q=85"
              alt="Professional Trademark Services"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0F1419]/80 via-transparent to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
              <h3 className="text-3xl font-bold mb-3 leading-tight">
                Expert Trademark Registration.<br/>
                <span className="text-[#FF9900]">Start at $45.</span>
              </h3>
              <p className="text-base text-gray-100">
                Professional trademark attorneys ready to protect your brand identity nationwide
              </p>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="w-full md:w-[55%] p-5 relative bg-white max-h-[90vh]">
            <div className="mb-3">
              <div className="inline-block bg-gradient-to-r from-[#FF9900] to-orange-600 text-black px-4 py-2 rounded-full text-sm font-bold mb-3">
                Special Offer: $45 Only!
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Start Your Trademark Registration</h2>
              <p className="text-sm text-gray-600">
                Get started today and protect your brand with our expert services
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-2.5">
              {/* Name */}
              <div className="space-y-1">
                <Label htmlFor="name" className="text-gray-700 font-semibold text-sm">Name *</Label>
                <Input
                  id="name"
                  placeholder="Enter your full name"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  required
                  className="border-2 border-[#FF9900]/50 focus:border-[#FF9900] focus:ring-2 focus:ring-[#FF9900]/50 h-10 bg-white transition-all"
                />
              </div>

              {/* Email */}
              <div className="space-y-1">
                <Label htmlFor="email" className="text-gray-700 font-semibold text-sm">Email *</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="your@email.com"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  onBlur={handleEmailBlur}
                  required
                  className={`border-2 focus:ring-2 focus:ring-[#FF9900]/50 h-10 bg-white transition-all ${
                    errors.email ? 'border-red-500' : 'border-[#FF9900]/50 focus:border-[#FF9900]'
                  }`}
                />
                {errors.email && (
                  <p className="text-red-500 text-xs mt-1">{errors.email}</p>
                )}
              </div>

              {/* Contact Number with Country Code */}
              <div className="space-y-1">
                <Label htmlFor="phone" className="text-gray-700 font-semibold text-sm">Contact number *</Label>
                <div className="flex gap-2">
                  <Select
                    value={formData.countryCode}
                    onValueChange={(value) => handleChange('countryCode', value)}
                  >
                    <SelectTrigger className="w-[120px] border-2 border-[#FF9900]/50 focus:border-[#FF9900] focus:ring-2 focus:ring-[#FF9900]/50 h-10 bg-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="max-h-[300px]">
                      {countryCodes.map((item) => (
                        <SelectItem key={item.code} value={item.code}>
                          <span className="flex items-center gap-2">
                            <span>{item.flag}</span>
                            <span>{item.code}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <div className="flex-1">
                    <Input
                      id="phone"
                      type="tel"
                      placeholder="1234567890"
                      value={formData.phone}
                      onChange={(e) => handlePhoneChange(e.target.value)}
                      required
                      maxLength={10}
                      className={`border-2 focus:ring-2 focus:ring-[#FF9900]/50 h-10 bg-white transition-all ${
                        errors.phone ? 'border-red-500' : 'border-[#FF9900]/50 focus:border-[#FF9900]'
                      }`}
                    />
                  </div>
                </div>
                {errors.phone && (
                  <p className="text-red-500 text-xs mt-1">{errors.phone}</p>
                )}
                <p className="text-gray-500 text-xs mt-1">Format: {formData.countryCode} + 10 digits (e.g., 1234567890)</p>
              </div>

              {/* Description */}
              <div className="space-y-1">
                <Label htmlFor="description" className="text-gray-700 font-semibold text-sm">Description</Label>
                <Textarea
                  id="description"
                  placeholder="What are you looking to protect? (e.g. Name, Logo and Slogan)"
                  rows={2}
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  className="border-2 border-[#FF9900]/50 focus:border-[#FF9900] focus:ring-2 focus:ring-[#FF9900]/50 resize-none bg-white transition-all"
                />
              </div>

              {/* Submit Button */}
              <div className="pt-2 pb-1">
                <Button
                  type="submit"
                  className="w-full bg-gradient-to-r from-[#FF9900] to-orange-600 hover:from-[#FF9900]/90 hover:to-orange-600/90 text-black font-bold h-10 text-base"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    'Register My Trademark Now →'
                  )}
                </Button>
              </div>
              
              <p className="text-xs text-gray-500 text-center pb-2">
                By submitting, you agree to our Terms and Privacy Policy.
              </p>
            </form>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ContactFormDialog;
